import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Download, ExternalLink, Sparkles, FileText, ImageIcon, ChevronRight, X, Filter, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import AnnotatedCommentViewer from "@/components/AnnotatedCommentViewer";
import SearchInput from "@/components/SearchInput";

const STATUSES = [
  { value: "new_request",  label: "New Request",   color: "bg-blue-100 text-blue-700" },
  { value: "on_process",   label: "On Process",    color: "bg-amber-100 text-amber-700" },
  { value: "for_approval", label: "For Approval",  color: "bg-violet-100 text-violet-700" },
  { value: "approved",     label: "Approved",      color: "bg-green-100 text-green-700" },
];
const labelFor = (s) => (STATUSES.find((x) => x.value === s) || {}).label || s;
const colorFor = (s) => (STATUSES.find((x) => x.value === s) || {}).color || "bg-neutral-100 text-neutral-600";

const AdminLayoutRequestsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [openRow, setOpenRow] = useState(null);   // lr_id
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      // Convert YYYY-MM-DD inputs to inclusive ISO bounds for the day
      if (startDate) params.set("start", `${startDate}T00:00:00`);
      if (endDate) params.set("end", `${endDate}T23:59:59`);
      const { data } = await api.get(`/admin/layout-requests?${params}`);
      setRows(data || []);
    } catch { toast.error("Failed to load layout requests"); }
    finally { setLoading(false); }
  }, [search, statusFilter, startDate, endDate]);
  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setSearch(""); setStatusFilter(""); setStartDate(""); setEndDate("");
  };
  const hasFilters = useMemo(
    () => !!(search || statusFilter || startDate || endDate),
    [search, statusFilter, startDate, endDate]
  );

  const openModal = async (lrId) => {
    setOpenRow(lrId);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/layout-requests/${lrId}`);
      setDetail(data);
    } catch { toast.error("Failed to load layout details"); setOpenRow(null); }
  };
  const closeModal = () => { setOpenRow(null); setDetail(null); };

  const updateStatus = async (newStatus) => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.patch(`/admin/layout-requests/${detail.lr_id}/status`, { status: newStatus });
      toast.success(`Status → ${labelFor(newStatus)}`);
      openModal(detail.lr_id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const uploadProcessed = async (file) => {
    if (!detail || !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/layout-requests/${detail.lr_id}/upload-processed`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded — customer notified for approval");
      openModal(detail.lr_id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const generateAILayout = () => {
    if (!detail) return;
    const o = detail.order || {};
    const first = (o.items || [])[0] || {};
    const params = new URLSearchParams();
    if (first.width)  params.set("width", first.width);
    if (first.height) params.set("height", first.height);
    if (detail.notes) params.set("prompt", detail.notes);
    if (detail.lr_id) params.set("lr_id", detail.lr_id);
    // Pass customer attachment URLs so /admin/ai-designer can auto-load them as references
    const refs = (detail.files || []).map((f) => f.url).filter(Boolean);
    if (refs.length) params.set("refs", refs.join("|"));
    navigate(`/admin/ai-designer?${params}`);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-layout-requests-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Design pipeline</p>
        <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F]">Layout Requests</h1>
        <p className="text-sm text-neutral-500 mt-1">Customer-requested designs awaiting your team.</p>
      </div>

      {/* Filters bar */}
      <div className="pav-card p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">Search</label>
            <div className="mt-1.5">
              <SearchInput
                value={search}
                onChange={setSearch}
                onEnter={load}
                placeholder="Search account, notes, order ID…"
                data-testid="layout-search" />
            </div>
          </div>
          <div className="min-w-[160px]">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">Status</label>
            <Select value={statusFilter || "_all"} onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="pav-input mt-1.5 h-10" data-testid="layout-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" /> From
            </label>
            <input type="date" value={startDate} max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="layout-date-start"
              className="pav-input h-10 mt-1.5 min-w-[150px]" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" /> To
            </label>
            <input type="date" value={endDate} min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="layout-date-end"
              className="pav-input h-10 mt-1.5 min-w-[150px]" />
          </div>
          {hasFilters && (
            <button onClick={clearFilters}
              data-testid="layout-clear-filters"
              className="btn-outline h-10 text-xs inline-flex items-center gap-1.5">
              <X className="h-3.5 w-3.5" /> Clear filters
            </button>
          )}
          <div className="ml-auto inline-flex items-center gap-2 text-xs text-neutral-500" data-testid="layout-result-count">
            <Filter className="h-3.5 w-3.5" />
            <span><span className="font-semibold text-[#0B1E3F]">{rows.length}</span> result{rows.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
      </div>

      <div className="pav-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="layout-table">
            <thead>
              <tr className="bg-[#0B1E3F] text-white text-left">
                <th className="px-4 py-3 font-semibold">Account name</th>
                <th className="px-4 py-3 font-semibold">Notes</th>
                <th className="px-4 py-3 font-semibold">Order</th>
                <th className="px-4 py-3 font-semibold">Date &amp; time</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-neutral-500" data-testid="layout-empty">No layout requests yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.lr_id} onClick={() => openModal(r.lr_id)}
                  data-testid={`layout-row-${r.lr_id}`}
                  className="border-b border-[#F4F6FB] cursor-pointer hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3">
                    <p className="font-semibold">{r.user_name || "—"}</p>
                    <p className="text-xs text-neutral-500">{r.user_email}</p>
                  </td>
                  <td className="px-4 py-3 max-w-[300px]"><p className="truncate">{r.notes}</p></td>
                  <td className="px-4 py-3 font-mono text-xs">{r.order_id}</td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${colorFor(r.status)}`}>{labelFor(r.status)}</span></td>
                  <td className="px-2 text-neutral-400"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!openRow} onOpenChange={(v) => !v && closeModal()}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="layout-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between flex-wrap gap-2">
              <span>{detail?.user_name || "—"} <span className="text-sm text-neutral-500 font-normal">· {detail?.order_id}</span></span>
              {detail && <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${colorFor(detail.status)}`} data-testid="layout-modal-status">{labelFor(detail.status)}</span>}
            </DialogTitle>
            <DialogDescription>{detail?.user_email}</DialogDescription>
          </DialogHeader>
          {!detail ? (
            <p className="text-center py-6"><Loader2 className="h-4 w-4 inline animate-spin" /> Loading…</p>
          ) : (
            <div className="space-y-4">
              {/* Brief */}
              <section>
                <h3 className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1">Customer brief</h3>
                <p className="whitespace-pre-wrap bg-[#F8FAFC] rounded-xl p-3 text-sm">{detail.notes}</p>
              </section>

              {/* Order summary */}
              <section>
                <h3 className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1">Order detail</h3>
                <div className="text-xs bg-[#F8FAFC] rounded-xl p-3 space-y-1">
                  <p><strong>Total:</strong> ₱{(detail.order?.total || 0).toLocaleString()}</p>
                  <p><strong>Fulfillment:</strong> {detail.order?.fulfillment_type}</p>
                  {(detail.order?.items || []).map((it, i) => (
                    <p key={i}>• {it.width}×{it.height} {it.unit} · {it.service_name || "—"} · ×{it.quantity}</p>
                  ))}
                </div>
              </section>

              {/* Customer files */}
              {detail.files?.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2">Customer attachments</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {detail.files.map((f, i) => (
                      <a key={i} href={fileUrl(f.url)} target="_blank" rel="noreferrer"
                        data-testid={`layout-file-thumb-${i}`}
                        className="block rounded-xl border border-[#E2E8F0] overflow-hidden hover:border-[#06B6D4]">
                        {(f.content_type || "").startsWith("image/") ? (
                          <img src={fileUrl(f.url)} alt={f.name} className="w-full h-24 object-cover" />
                        ) : (
                          <div className="h-24 flex items-center justify-center bg-[#F4F6FB]"><FileText className="h-7 w-7 text-[#0B1E3F]" /></div>
                        )}
                        <p className="p-2 text-[11px] truncate font-semibold">{f.name}</p>
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* Processed files */}
              {detail.processed_files?.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2">Your uploaded layouts</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {detail.processed_files.map((f, i) => (
                      <a key={i} href={fileUrl(f.url)} target="_blank" rel="noreferrer"
                        data-testid={`layout-processed-thumb-${i}`}
                        className="block rounded-xl border-2 border-[#06B6D4] overflow-hidden hover:border-[#0B1E3F]">
                        {(f.content_type || "").startsWith("image/") ? (
                          <img src={fileUrl(f.url)} alt={f.name} className="w-full h-24 object-cover" />
                        ) : (
                          <div className="h-24 flex items-center justify-center bg-[#F4F6FB]"><ImageIcon className="h-7 w-7 text-[#0B1E3F]" /></div>
                        )}
                        <p className="p-2 text-[11px] truncate font-semibold">{f.name}</p>
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* Comments timeline */}
              <section>
                <h3 className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2">Comments / log</h3>
                <ul className="space-y-2 text-xs">
                  {(detail.comments || []).length === 0 && <li className="text-neutral-400">No comments yet.</li>}
                  {(detail.comments || []).map((c) => (
                    <li key={c.id} className="bg-[#F8FAFC] rounded-lg p-2">
                      <p className="font-semibold capitalize">{c.role} · <span className="text-neutral-500">{c.by}</span></p>
                      {c.text && <p className="whitespace-pre-wrap">{c.text}</p>}
                      {c.annotations?.length > 0 && (
                        <AnnotatedCommentViewer
                          annotations={c.annotations}
                          imageUrl={c.image_url}
                          testid={`admin-comment-annot-${c.id}`} />
                      )}
                      <p className="text-[10px] text-neutral-400 mt-1">{new Date(c.at).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Action buttons */}
              <div className="flex flex-wrap items-center gap-2 border-t border-[#E2E8F0] pt-4">
                <button onClick={() => fileInputRef.current?.click()} disabled={busy}
                  className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="layout-upload-processed-btn">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload processed layout
                </button>
                <input ref={fileInputRef} type="file" className="hidden"
                  onChange={(e) => uploadProcessed(e.target.files?.[0])} />
                <button onClick={generateAILayout}
                  className="btn-outline text-sm inline-flex items-center gap-1.5"
                  data-testid="layout-generate-ai-btn">
                  <Sparkles className="h-3.5 w-3.5" /> Generate AI Layout
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-neutral-500">Status:</span>
                  <Select value={detail.status} onValueChange={updateStatus}>
                    <SelectTrigger className="pav-input h-9 min-w-[160px]" data-testid="layout-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLayoutRequestsPage;
